import { useEffect, useMemo, useRef, useState, FormEvent } from 'react';
import { supabase } from '../supabase';
import { EligibleRule } from '../../types/discounts';
import { analytics } from '../../utils/analytics';

type Scope = 'school' | 'parent' | 'student';

interface PersonOption {
  id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
}

interface DiscountConfigOption {
  id: string;
  name: string;
  subtype: string | null;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful save so the parent can refetch. */
  onSaved: () => void;
  /** Rule being edited, or null when adding a new rule. */
  rule: EligibleRule | null;
  schools: string[];
  discountConfigs: DiscountConfigOption[];
  /** Full parent/student lists, used to resolve names when prefilling an edit. */
  parents: PersonOption[];
  students: PersonOption[];
}

const SCOPES: { value: Scope; label: string; emoji: string }[] = [
  { value: 'student', label: 'Student', emoji: '🧒' },
  { value: 'parent', label: 'Parent', emoji: '👨‍👩‍👧‍👦' },
  { value: 'school', label: 'School', emoji: '🏫' },
];

const DISCOUNT_TYPES = [
  { value: 'percentage', label: 'Percentage' },
  { value: 'fixed_amount', label: 'Fixed Amount' },
];

function personLabel(p: PersonOption): string {
  const name = `${p.first_name ?? ''} ${p.last_name ?? ''}`.trim();
  return p.email ? `${name} (${p.email})` : name || p.email || p.id;
}

/**
 * Shared add/edit modal for an eligible discount rule (school-, parent-, or
 * student-based). Renders as a bottom sheet on mobile and a centered dialog on
 * desktop. The scope selector swaps the target picker; scope is locked while
 * editing an existing rule.
 */
export function EligibleRuleModal({
  isOpen,
  onClose,
  onSaved,
  rule,
  schools,
  discountConfigs,
  parents,
  students,
}: Props) {
  const isEdit = !!rule;

  const [scope, setScope] = useState<Scope>('student');
  const [school, setSchool] = useState('');
  const [selectedPeople, setSelectedPeople] = useState<PersonOption[]>([]);
  const [discountType, setDiscountType] = useState<'percentage' | 'fixed_amount'>('percentage');
  const [discountValue, setDiscountValue] = useState('0');
  const [notes, setNotes] = useState('');
  const [lineItemConfigId, setLineItemConfigId] = useState('');

  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<PersonOption[]>([]);
  const [searching, setSearching] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Prefill when the modal opens (or the rule being edited changes).
  useEffect(() => {
    if (!isOpen) return;
    const r = rule;
    const initScope: Scope = r ? r.type : 'student';
    setScope(initScope);
    setDiscountType(r ? r.discount_type : 'percentage');
    setDiscountValue(r ? String(r.discount_value) : '0');
    setNotes(r?.notes ?? '');
    setLineItemConfigId(r?.line_item_config_id ?? '');
    setSearch('');
    setSearchResults([]);
    setError(null);

    if (r && r.type === 'school') {
      setSchool(r.value);
      setSelectedPeople([]);
    } else if (r && (r.type === 'parent' || r.type === 'student')) {
      setSchool('');
      let ids: string[] = [];
      try {
        ids = JSON.parse(r.value);
      } catch {
        ids = [];
      }
      const pool = r.type === 'parent' ? parents : students;
      setSelectedPeople(ids.map(id => pool.find(p => p.id === id) ?? { id }));
    } else {
      setSchool('');
      setSelectedPeople([]);
    }
  }, [isOpen, rule, parents, students]);

  // Debounced search for parent/student scopes.
  useEffect(() => {
    if (scope === 'school' || search.length < 2) {
      setSearchResults([]);
      return;
    }
    const timeoutId = setTimeout(async () => {
      try {
        setSearching(true);
        let results: PersonOption[] = [];
        if (scope === 'parent') {
          const { data, error: searchError } = await supabase
            .from('parents')
            .select('id, first_name, last_name, email')
            .or(`first_name.ilike.%${search}%,last_name.ilike.%${search}%,email.ilike.%${search}%`)
            .eq('active', true)
            .limit(10);
          if (searchError) throw searchError;
          results = (data ?? []) as PersonOption[];
        } else {
          const { data, error: searchError } = await supabase
            .from('students')
            .select('id, first_name, last_name')
            .or(`first_name.ilike.%${search}%,last_name.ilike.%${search}%`)
            .limit(10);
          if (searchError) throw searchError;
          results = (data ?? []) as PersonOption[];
        }
        const selectedIds = new Set(selectedPeople.map(p => p.id));
        setSearchResults(results.filter(p => !selectedIds.has(p.id)));
      } catch (err) {
        console.error('Error searching:', err);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [search, scope, selectedPeople]);

  // Configs scoped to the selected type, falling back to all if none match.
  const configOptions = useMemo(() => {
    const scoped = discountConfigs.filter(c => c.subtype === scope);
    return scoped.length > 0 ? scoped : discountConfigs;
  }, [discountConfigs, scope]);

  const overlayRef = useRef<HTMLDivElement>(null);

  if (!isOpen) return null;

  const addPerson = (p: PersonOption) => {
    if (!selectedPeople.some(s => s.id === p.id)) {
      setSelectedPeople(prev => [...prev, p]);
    }
    setSearch('');
    setSearchResults([]);
  };
  const removePerson = (id: string) => {
    setSelectedPeople(prev => prev.filter(p => p.id !== id));
  };

  const handleScopeChange = (next: Scope) => {
    setScope(next);
    setSchool('');
    setSelectedPeople([]);
    setSearch('');
    setSearchResults([]);
    setLineItemConfigId('');
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      if (scope === 'school' && !school) throw new Error('Please select a school');
      if (scope !== 'school' && selectedPeople.length === 0) {
        throw new Error(`Please select at least one ${scope}`);
      }
      if (!lineItemConfigId) throw new Error('Please select a discount line item config');

      const value = scope === 'school'
        ? school
        : JSON.stringify(selectedPeople.map(p => p.id));

      const payload = {
        type: scope,
        value,
        discount_type: discountType,
        discount_value: Number(discountValue),
        notes: notes || null,
        line_item_config_id: lineItemConfigId,
        active: true,
      };

      if (isEdit && rule) {
        const { error: saveError } = await supabase
          .from('eligible_rules')
          .update(payload)
          .eq('id', rule.id);
        if (saveError) throw saveError;
      } else {
        const { error: saveError } = await supabase.from('eligible_rules').insert([payload]);
        if (saveError) throw saveError;
      }
      // These apply automatically at invoice generation, so a rule change moves money on
      // every future invoice that matches it.
      analytics.trackAdminAction(isEdit ? 'admin_eligible_rule_update' : 'admin_eligible_rule_create', {
        entityType: 'eligible_rule',
        entityId: isEdit && rule ? rule.id : undefined,
        metadata: {
          scope: payload.type,
          value: payload.value,
          discount_type: payload.discount_type,
          discount_value: payload.discount_value,
        },
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save rule');
    } finally {
      setLoading(false);
    }
  };

  const scopeNoun = scope === 'parent' ? 'parent' : 'student';

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 bg-gray-500 bg-opacity-75 flex flex-col justify-end sm:items-center sm:justify-center z-[60]"
      onClick={e => { if (e.target === overlayRef.current) onClose(); }}
    >
      <div className="bg-white p-6 rounded-t-2xl sm:rounded-lg shadow-xl sm:max-w-lg w-full max-h-[85vh] overflow-y-auto">
        <div className="sm:hidden flex justify-center -mt-2 mb-4">
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          {isEdit ? 'Edit discount rule' : 'Add discount rule'}
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Applied automatically during invoice creation.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Scope selector */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">Applies to</label>
            <div className="grid grid-cols-3 gap-2">
              {SCOPES.map(s => (
                <button
                  key={s.value}
                  type="button"
                  disabled={isEdit && s.value !== scope}
                  onClick={() => handleScopeChange(s.value)}
                  className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${
                    scope === s.value
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                  } ${isEdit && s.value !== scope ? 'opacity-40 cursor-not-allowed' : ''}`}
                >
                  <span>{s.emoji}</span> {s.label}
                </button>
              ))}
            </div>
            {isEdit && (
              <p className="text-xs text-gray-400 mt-1">Scope can't be changed when editing.</p>
            )}
          </div>

          {/* Target picker */}
          {scope === 'school' ? (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">School</label>
              <select
                value={school}
                onChange={e => setSchool(e.target.value)}
                className="block w-full border-gray-300 rounded-md text-sm"
                required
              >
                <option value="">Select school</option>
                {schools.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          ) : (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {scope === 'parent' ? 'Parent(s)' : 'Student(s)'}
              </label>
              {selectedPeople.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {selectedPeople.map(p => (
                    <span key={p.id} className="flex items-center gap-1 bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-sm">
                      {personLabel(p)}
                      <button type="button" onClick={() => removePerson(p.id)} className="text-blue-600 hover:text-blue-800 ml-1">×</button>
                    </span>
                  ))}
                </div>
              )}
              <div className="relative">
                <input
                  type="text"
                  placeholder={`Search for ${scopeNoun}s...`}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                />
                {searchResults.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {searchResults.map(p => (
                      <div
                        key={p.id}
                        className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                        onClick={() => addPerson(p)}
                      >
                        <div>
                          <div className="text-sm font-medium text-gray-900">{`${p.first_name ?? ''} ${p.last_name ?? ''}`.trim()}</div>
                          {p.email && <div className="text-xs text-gray-500">{p.email}</div>}
                        </div>
                        <button type="button" className="text-sm text-blue-600 hover:text-blue-800">Add</button>
                      </div>
                    ))}
                  </div>
                )}
                {search.length >= 2 && !searching && searchResults.length === 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg p-3 text-sm text-gray-500">
                    No {scopeNoun}s found.
                  </div>
                )}
                {searching && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg p-3 text-sm text-gray-500">
                    Searching...
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Discount type + value */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Discount Type</label>
              <select
                value={discountType}
                onChange={e => setDiscountType(e.target.value as 'percentage' | 'fixed_amount')}
                className="block w-full border-gray-300 rounded-md text-sm"
              >
                {DISCOUNT_TYPES.map(dt => <option key={dt.value} value={dt.value}>{dt.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Value {discountType === 'percentage' ? '(%)' : '($)'}
              </label>
              <input
                type="number"
                value={discountValue}
                onChange={e => setDiscountValue(e.target.value)}
                className="block w-full border-gray-300 rounded-md text-sm"
                min="0"
                step="0.01"
                required
              />
            </div>
          </div>

          {/* Config */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Discount Line Item Config</label>
            <select
              value={lineItemConfigId}
              onChange={e => setLineItemConfigId(e.target.value)}
              className="block w-full border-gray-300 rounded-md text-sm"
              required
            >
              <option value="">Select config</option>
              {configOptions.map(cfg => (
                <option key={cfg.id} value={cfg.id}>{cfg.name}{cfg.subtype ? ` (${cfg.subtype})` : ''}</option>
              ))}
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Notes (optional)</label>
            <input
              type="text"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              className="block w-full border-gray-300 rounded-md text-sm"
              placeholder="Shown on invoice description"
            />
          </div>

          {error && <div className="text-red-600 text-sm">{error}</div>}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-sm font-medium"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
              disabled={loading}
            >
              {loading ? 'Saving...' : isEdit ? 'Update' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
